import { observer } from 'mobx-react-lite';
import { useLessonStore } from '../provider/LessonProvider';
import { useChapterStore } from '../provider/ChapterProvider';
import { useEffect, useState } from 'react';
import _ from 'lodash';

/**
 * 재생 진행 막대
 * - startTime ~ endTime 범위에서 currentTime의 진행률을 표시
 * - 범위 밖이면 표시하지 않음
 */
const PlaybackBar = observer((): React.ReactNode => {
    const lessonStore = useLessonStore();
    const chapterStore = useChapterStore();

    const isAutoPlaying = chapterStore.isAutoPlaying;
    const isSentencePlaying = chapterStore.isSentencePlaying;

    const chapterStartTime = chapterStore.chapterStartTime;
    const chapterEndTime = chapterStore.chapterEndTime;
    const sentenceStartTime = chapterStore.currentSentence?.startTime ?? 0;
    const sentenceEndTime = chapterStore.currentSentence?.endTime ?? 0;

    const currentTime = chapterStore.currentPlaybackTime;

    // [중요] 플레이어 시간 업데이트 따른 콜백 처리
    // - 기본 문장 재생일 때: 재생되다가 문장 종료 시점에 재생 종료
    // - 자동 재생 모드일 때: 문장 자동 전환되고, 마지막 문장 종료 시점에 재생 종료
    useEffect(() => {
        // 자동 재생 모드일 때만 문장 자동 전환
        if (isAutoPlaying) {
            // 챕터 종료 체크
            // 같은 것보다 길게 해줘야 끝까지 잘 들림
            if (currentTime > chapterEndTime) {
                chapterStore.stopAutoPlay();
                lessonStore?.lessonPlayerRef?.current?.pause();
                return;
            }

            // 재생 시간에 해당하는 문장으로 이동
            chapterStore.moveToCurrentPlaybackTimeSentence();
        } else if (isSentencePlaying) {
            // 문장 재생 모드일 때는 문장 시간이 다 되면 종료
            if (chapterStore.currentSentence && currentTime > sentenceEndTime) {
                lessonStore?.lessonPlayerRef?.current?.pause();
                chapterStore.stopSentencePlaying();
            }
        }
    }, [
        lessonStore,
        chapterStore,
        currentTime,
        isAutoPlaying,
        isSentencePlaying,
        chapterEndTime,
        sentenceStartTime,
        sentenceEndTime,
    ]);

    let start = 0;
    let end = 0;

    if (isAutoPlaying) {
        start = chapterStartTime;
        end = chapterEndTime;
    } else {
        start = sentenceStartTime;
        end = sentenceEndTime;
    }

    // 범위 밖이면 표시 안함
    let isOutOfRange = false;
    const total = end - start;
    const elapsed = currentTime - start;
    const progress = Math.min(1, Math.max(0, elapsed / total));

    if (currentTime < start || currentTime > end || end - start <= 0) {
        isOutOfRange = true;
    }

    // 마우스 호버 시 원형 인디케이터 위치
    const [hoverPosition, setHoverPosition] = useState<number | null>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const ratio = x / rect.width;
        setHoverPosition(Math.min(1, Math.max(0, ratio)) * 100);
    };

    const handleMouseLeave = () => {
        setHoverPosition(null);
    };

    return (
        <div
            className="relative flex h-5 w-full cursor-pointer items-center justify-center"
            onClick={(e) => {
                // 클릭한 위치의 시간을 찾아서 재생한다
                const currentTarget = e.currentTarget as HTMLDivElement;
                const rect = currentTarget.getBoundingClientRect();
                const startX = rect.left;
                const width = rect.width;
                const ratio = (e.clientX - startX) / width;

                const clickedTime = start + ratio * total;

                lessonStore?.lessonPlayerRef?.current?.seekToAndPlay(clickedTime);

                // 문장 시간 내에서 재생하는 것이지만
                // 플레이어에서 다른 시간대를 재생하다가 돌아왔을 수도 있으므로 딜레이를 줘야 함
                _.delay(() => {
                    chapterStore.startSentencePlaying();
                }, 200);
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <div
                // 재생 막대 바
                className="bg-primary/20 h-1 w-full overflow-hidden rounded-full"
            >
                {!isOutOfRange && (
                    <div
                        className="bg-primary h-full transition-[width] duration-75"
                        style={{ width: `${progress * 100}%` }}
                    />
                )}
            </div>
            {/* 호버 시 원형 인디케이터 (막대 바 위에 표시) */}
            {hoverPosition !== null && (
                <div
                    className="bg-primary pointer-events-none absolute top-0 size-1 -translate-x-1/2 rounded-full"
                    style={{ left: `${hoverPosition}%` }}
                />
            )}
        </div>
    );
});

export default PlaybackBar;
